import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, Mail, MapPin, MessageSquare, Save, X, Trash2, AlertTriangle, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import HeaderNav from '@/components/HeaderNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeadDetail() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [newComment, setNewComment] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [allowedStatuses, setAllowedStatuses] = useState([]); // Phase 1A: Dynamic allowed statuses
  const [isTerminalStatus, setIsTerminalStatus] = useState(false); // Phase 1A: Terminal status check
  const [deadReasons, setDeadReasons] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allSources, setAllSources] = useState([]); // For Super Admin source editing
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Phase 1A: Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Merge dialog for duplicates
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  
  // Status change dialog
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusData, setStatusData] = useState({
    next_call_date: '',     // For Attempted 1-3, Follow up, Site Visit Planned
    site_visit_number: '',  // For Site Visit Done (1-4)
    site_visit_date: '',    // For Site Visit Done (date only)
    tentative_visit_date: '',
    tentative_visit_time: '',
    dead_reason: '',
    comment: ''
  });

  // Site Visit Tracking state
  const [siteVisitTracking, setSiteVisitTracking] = useState({ selectedVisit: '' });
  const [showSiteVisitDialog, setShowSiteVisitDialog] = useState(false);
  
  // Activities state
  const [activities, setActivities] = useState([]);

  const isAdmin = user?.user_role === 'Super Administrator' || user?.user_role === 'Manager';
  const isSuperAdmin = user?.user_role === 'Super Administrator';

  // Helper function to get available visit options based on existing visits
  const getAvailableVisitOptions = (siteVisits) => {
    const recordedVisits = siteVisits.map(v => v.visit_number);
    const allVisits = [1, 2, 3, 4];
    return allVisits
      .filter(num => !recordedVisits.includes(num))
      .map(num => ({ value: num.toString(), label: `Visit #${num}` }));
  };

  useEffect(() => {
    // Save current pathname to sessionStorage so LeadList can detect navigation back
    const currentPath = `/leads/${id}`;
    sessionStorage.setItem('lastPathname', currentPath);
    
    loadLead();
    loadMasterData();
  }, [id]);

  const loadActivities = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/activities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data || []);
    } catch (error) {
      console.error('Failed to load activities:', error);
      setActivities([]);
    }
  };

  const loadLead = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLead(response.data);
      setFormData(response.data);
      setSelectedProjects(response.data.projects || []);
      
      // Phase 1A: Load allowed statuses dynamically
      loadAllowedStatuses(response.data.lead_status);
      
      // Load activities
      loadActivities();
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  // Phase 1A: Load dynamically allowed statuses
  const loadAllowedStatuses = async (currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/leads/${id}/allowed-statuses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllowedStatuses(response.data.allowed_statuses);
      setIsTerminalStatus(response.data.is_terminal);
    } catch (error) {
      console.error('Failed to load allowed statuses');
    }
  };

  const loadMasterData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [statusesRes, reasonsRes, brokersRes, projectsRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/master/lead-statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/master/dead-reasons`, { headers: { Authorization: `Bearer ${token}` } }),
        // CHANGE 4: Use active brokers endpoint for assignment dropdown
        axios.get(`${API}/brokers/active`, { headers: { Authorization: `Bearer ${token}` } }),
        // MODIFICATION #1: Sales Reps see only assigned projects
        axios.get(`${API}/projects/assigned`, { headers: { Authorization: `Bearer ${token}` } }),
        // Load sources for Super Admin source editing
        axios.get(`${API}/master/sources`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStatuses(statusesRes.data);
      setDeadReasons(reasonsRes.data);
      setBrokers(brokersRes.data);
      setAllProjects(projectsRes.data);
      setAllSources(sourcesRes.data);
    } catch (error) {
      console.error('Failed to load master data');
    }
  };

  // Phase 1A: Use dynamic allowed statuses instead of hardcoded logic
  const handleStatusChange = (leadId, currentStatus, newStatus) => {
    // Allow selecting "Follow up" again to update next_call_date
    const isReschedulingFollowUp = newStatus === 'Follow up' && currentStatus === 'Follow up';
    
    if (newStatus === currentStatus && !isReschedulingFollowUp) return;
    
    const today = new Date().toISOString().split('T')[0];
    const now = new Date().toTimeString().split(' ')[0].substring(0, 5);
    
    setStatusData({
      attempted_date: today,
      attempted_time: now,
      attempted_comments: '',
      tentative_visit_date: today,
      tentative_visit_time: now,
      visit_date: today,
      visit_time: now,
      visit_remarks: '',
      follow_up_date: today,
      follow_up_time: now,
      dead_reason: '',
      comments: '',
      // Pre-fill with current next_call_date for rescheduling
      next_call_date: isReschedulingFollowUp && lead.next_call_date ? lead.next_call_date.split('T')[0] : '',
      isRescheduling: isReschedulingFollowUp  // Flag to indicate this is a reschedule
    });
    setSelectedStatus(newStatus);
    setShowStatusDialog(true);
  };

  const handleSaveStatus = async () => {
    const token = localStorage.getItem('token');
    
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      // Check if this is just a reschedule (same status, updating date only)
      const isRescheduling = statusData.isRescheduling && selectedStatus === lead.lead_status;
      
      let updateData = {};
      
      if (isRescheduling) {
        // Only update next_call_date, don't change status
        if (!statusData.next_call_date) {
          toast.error('Next call date is required');
          return;
        }
        updateData = { 
          next_call_date: statusData.next_call_date,
          reschedule_only: true,  // Flag for backend to log as date update
          old_next_call_date: lead.next_call_date  // For activity log
        };
        
        // Add comment if provided (optional - can be empty)
        if (statusData.comment !== undefined && statusData.comment !== null) {
          updateData.comment = statusData.comment.trim() || '';
        }
      } else {
        // Normal status change
        updateData = { lead_status: selectedStatus };
        
        // Add conditional fields based on status
        const statusesRequiringNextCall = ['Attempted - 1', 'Attempted - 2', 'Attempted - 3', 'Follow up', 'Site Visit Planned'];
        
        if (statusesRequiringNextCall.includes(selectedStatus)) {
          if (!statusData.next_call_date) {
            toast.error('Next call date is required for this status');
            return;
          }
          updateData.next_call_date = statusData.next_call_date;
        }
        
        if (selectedStatus === 'Site Visit Done') {
          if (!statusData.site_visit_number || !statusData.site_visit_date) {
            toast.error('Site visit number and date are required');
            return;
          }
          updateData.site_visit_number = parseInt(statusData.site_visit_number);
          updateData.site_visit_date = statusData.site_visit_date;
        }
        
        if (selectedStatus === 'Site Visit Planned') {
          if (!statusData.tentative_visit_date) {
            toast.error('Tentative visit date is required');
            return;
          }
          updateData.tentative_visit_date = statusData.tentative_visit_date;
        }
        
        if (['Lost', 'Unqualified', 'Dropped after site visit'].includes(selectedStatus)) {
          if (!statusData.dead_reason) {
            toast.error('Dead reason is required for this status');
            return;
          }
          updateData.dead_reason = statusData.dead_reason;
        }
        
        // Add comment if provided (optional - can be empty)
        if (statusData.comment !== undefined && statusData.comment !== null) {
          updateData.comment = statusData.comment.trim() || '';
        }
      }
      
      await axios.put(`${API}/leads/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRescheduling ? 'Next call date updated successfully' : 'Lead status updated successfully');
      setShowStatusDialog(false);
      // Reset status data
      setStatusData({
        next_call_date: '',
        site_visit_number: '',
        site_visit_date: '',
        tentative_visit_date: '',
        tentative_visit_time: '',
        dead_reason: '',
        comment: '',
        isRescheduling: false
      });
      loadLead();
      loadActivities();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or string errors
      let errorMessage = 'Failed to update';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    }
  };

  const handleProjectToggle = (projectName) => {
    setSelectedProjects(prev => 
      prev.includes(projectName)
        ? prev.filter(p => p !== projectName)
        : [...prev, projectName]
    );
  };

  const handleUpdate = async () => {
    if (selectedProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    const token = localStorage.getItem('token');
    let updates = { ...formData, projects: selectedProjects };
    
    // BUGFIX: Sales Reps cannot send customer_name in updates (backend validation)
    if (user?.user_role === 'Sales Rep') {
      delete updates.customer_name;
    }
    
    try {
      await axios.put(`${API}/leads/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead updated successfully');
      setEditing(false);
      loadLead();
    } catch (error) {
      console.error('Lead update error:', error.response?.data || error.message);
      // Handle Pydantic validation errors (array of objects) or string errors
      let errorMessage = 'Failed to update lead';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format: [{type, loc, msg, input, ctx}]
        errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/leads/${id}/comments`, { comment: newComment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comment added');
      setNewComment('');
      loadLead();
      loadActivities();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Phase 1A: Delete lead function
  const handleDeleteLead = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead deleted successfully');
      navigate('/leads');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete lead';
      toast.error(errorMsg);
      setShowDeleteDialog(false);
    }
  };

  // Merge duplicate lead with parent
  const handleMergeDuplicate = async () => {
    if (!lead.parent_lead_id) {
      toast.error('Parent lead not found');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      // Get parent lead
      const parentResponse = await axios.get(`${API}/leads/${lead.parent_lead_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const parentLead = parentResponse.data;

      // Merge projects
      const mergedProjects = [...new Set([...parentLead.projects, ...lead.projects])];
      
      // Update parent lead with merged projects
      await axios.put(`${API}/leads/${lead.parent_lead_id}`, 
        { projects: mergedProjects },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Delete duplicate lead
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Leads merged successfully');
      navigate('/leads');
    } catch (error) {
      toast.error('Failed to merge leads');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-700',
      'Booked': 'bg-green-100 text-green-700',
      'Lost': 'bg-red-100 text-red-700',
      'Site Visit Planned': 'bg-purple-100 text-purple-700',
      'Site Visit Done': 'bg-indigo-100 text-indigo-700',
      'Follow up': 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!lead) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lead-detail-page">
      <HeaderNav title={lead.customer_name} />

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Lead Number: {lead.lead_number}</p>
                <p className="text-sm text-slate-600 mt-1">Assigned to: {lead.assigned_to_name}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={getStatusColor(lead.lead_status)}>{lead.lead_status}</Badge>
                {!editing && (
                  <Button 
                    onClick={() => setEditing(true)} 
                    size="sm" 
                    data-testid="edit-button"
                    disabled={lead.duplicate_flag && lead.customer_name && lead.mobile}
                  >
                    Edit Details
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="contact-info-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Name</Label>
                {editing ? (
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    data-testid="customer-name-input"
                    disabled={user?.user_role === 'Sales Rep'}
                    placeholder={user?.user_role === 'Sales Rep' ? 'Sales Reps cannot edit lead name' : ''}
                  />
                ) : (
                  <div className="flex items-center text-slate-700">
                    <span className="font-medium">{lead.customer_name}</span>
                  </div>
                )}
                {editing && user?.user_role === 'Sales Rep' && (
                  <p className="text-xs text-slate-500">Contact your manager to change lead name</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Mobile</Label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <a href={`tel:${lead.mobile}`} className="text-cyan-600 hover:underline font-medium">
                    {lead.mobile}
                  </a>
                </div>
              </div>

              {lead.email && (
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <a href={`mailto:${lead.email}`} className="text-cyan-600 hover:underline">
                      {lead.email}
                    </a>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Source</Label>
                {editing && isSuperAdmin ? (
                  <Select 
                    value={formData.source || lead.source || ''} 
                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {allSources.map(src => (
                        <SelectItem key={src.value} value={src.value}>
                          {src.label || src.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-slate-700 font-medium">{lead.source}</div>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Projects Interested</Label>
              {editing ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50 max-h-60 overflow-y-auto">
                  {allProjects.filter(p => p.active_status).map(project => (
                    <div key={project.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`project-${project.id}`}
                        checked={selectedProjects.includes(project.project_name)}
                        onCheckedChange={() => handleProjectToggle(project.project_name)}
                      />
                      <label
                        htmlFor={`project-${project.id}`}
                        className="text-sm leading-none cursor-pointer"
                      >
                        {project.project_name}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {lead.projects?.map((project, idx) => (
                    <Badge key={idx} variant="outline" className="bg-slate-100">
                      <MapPin className="h-3 w-3 mr-1" />
                      {project}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Sales Rep Assignment (Super Admin only) */}
        {(user?.user_role === 'Super Administrator' || user?.user_role === 'Manager') && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Sales Rep Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>Assigned Sales Rep</Label>
                {editing ? (
                  <Select 
                    value={formData.assigned_to || lead.assigned_to || ''} 
                    onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sales rep" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* CHANGE 4: brokers now only contains active sales reps */}
                      {brokers.map(broker => (
                        <SelectItem key={broker.id} value={broker.id}>
                          {broker.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-100">
                      {lead.assigned_to_name}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase 1A: Additional Lead Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* BUG FIX #2: Status dropdown exactly like All Leads table */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-3 border-b">
              <div>
                <Label className="text-slate-600">Lead Status</Label>
                <select
                  value={lead.lead_status}
                  onChange={(e) => handleStatusChange(lead.id, lead.lead_status, e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                  disabled={lead.duplicate_flag}
                >
                  <option value={lead.lead_status}>{lead.lead_status}</option>
                  {/* Add "Reschedule Follow up" option when current status is Follow up */}
                  {lead.lead_status === 'Follow up' && (
                    <option value="Follow up">📅 Reschedule Follow up</option>
                  )}
                  {allowedStatuses
                    .filter(s => s !== lead.lead_status)
                    .map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                </select>
              </div>
              
              {/* Site Visit Tracking Section - Active when status is Site Visit Done */}
              {lead.lead_status === 'Site Visit Done' && (
                <div>
                  <Label className="text-slate-600">Site Visit Tracking</Label>
                  <div className="flex gap-2">
                    <select
                      value={siteVisitTracking.selectedVisit}
                      onChange={(e) => setSiteVisitTracking({ ...siteVisitTracking, selectedVisit: e.target.value })}
                      className="flex-1 px-3 py-2 border rounded-md text-sm"
                    >
                      <option value="">Select visit to record...</option>
                      {getAvailableVisitOptions(lead.site_visits || []).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={() => setShowSiteVisitDialog(true)}
                      disabled={!siteVisitTracking.selectedVisit}
                      className="whitespace-nowrap"
                    >
                      Record Visit
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Lead Created Date - Editable by Super Admin */}
              <div>
                <Label className="text-slate-600">Lead Created Date</Label>
                {editing && isSuperAdmin ? (
                  <Input
                    type="datetime-local"
                    value={formData.created_at ? formData.created_at.slice(0, 16) : (lead.created_at ? lead.created_at.slice(0, 16) : '')}
                    onChange={(e) => setFormData({ ...formData, created_at: e.target.value ? new Date(e.target.value).toISOString() : lead.created_at })}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-slate-800 font-medium">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB') + ' ' + new Date(lead.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                  </p>
                )}
              </div>
              
              {lead.entry_method && (
                <div>
                  <Label className="text-slate-600">Entry Method</Label>
                  <p className="text-slate-800 font-medium">{lead.entry_method}</p>
                </div>
              )}
              
              {lead.location && (
                <div>
                  <Label className="text-slate-600">Location</Label>
                  <p className="text-slate-800 font-medium">{lead.location}</p>
                </div>
              )}
              
              {lead.budget_range && (
                <div>
                  <Label className="text-slate-600">Budget Range</Label>
                  <p className="text-slate-800 font-medium">{lead.budget_range}</p>
                </div>
              )}
              
              {lead.property_type && (
                <div>
                  <Label className="text-slate-600">Property Type</Label>
                  <p className="text-slate-800 font-medium">{lead.property_type}</p>
                </div>
              )}
              
              {lead.campaign_name && (
                <div>
                  <Label className="text-slate-600">Campaign</Label>
                  <p className="text-slate-800 font-medium">{lead.campaign_name}</p>
                </div>
              )}
              
              {lead.sub_source && (
                <div>
                  <Label className="text-slate-600">Sub Source</Label>
                  <p className="text-slate-800 font-medium">{lead.sub_source}</p>
                </div>
              )}
              
              {lead.next_call_date && (
                <div>
                  <Label className="text-slate-600">Next Call Date</Label>
                  <p className="text-slate-800 font-medium">{new Date(lead.next_call_date).toLocaleDateString('en-GB')}</p>
                </div>
              )}
              
              {lead.last_call_date && (
                <div>
                  <Label className="text-slate-600">Last Call Date</Label>
                  <p className="text-slate-800 font-medium">{new Date(lead.last_call_date).toLocaleDateString('en-GB')} {new Date(lead.last_call_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                </div>
              )}
              
              {lead.duplicate_flag && (
                <div className="md:col-span-2 space-y-3">
                  <Badge variant="destructive" className="bg-red-100 text-red-700">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Duplicate Lead
                  </Badge>
                  {lead.parent_lead_number && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-amber-900">Parent Lead: {lead.parent_lead_number}</p>
                      <p className="text-xs text-amber-700 mt-1">This is a duplicate of the above lead</p>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => setShowMergeDialog(true)}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          <GitMerge className="mr-1 h-3 w-3" />
                          Merge with Parent
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setShowDeleteDialog(true)}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {editing && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location || ''}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="City/Area"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Budget Range</Label>
                  <Input
                    value={formData.budget_range || ''}
                    onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                    placeholder="e.g. 30-40 Lac"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Property Type</Label>
                  <Input
                    value={formData.property_type || ''}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    placeholder="e.g. 2 BHK"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Site Visit History */}
        {lead?.site_visits && lead.site_visits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Site Visit History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium text-slate-600">Visit #</th>
                      <th className="text-left p-2 font-medium text-slate-600">Visit Date</th>
                      <th className="text-left p-2 font-medium text-slate-600">Marked On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lead.site_visits.map((visit, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="p-2">Visit {visit.visit_number}</td>
                        <td className="p-2">{new Date(visit.visit_date).toLocaleDateString('en-GB')}</td>
                        <td className="p-2">
                          Marked on {new Date(visit.marked_at).toLocaleDateString('en-GB')} {new Date(visit.marked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* BUG FIX #1: Completely removed "Lead Details" card from edit mode */}

        <Card data-testid="comments-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center">
              <MessageSquare className="mr-2 h-5 w-5" />
              Comments & Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                data-testid="comment-textarea"
                rows={3}
              />
              <Button onClick={handleAddComment} size="sm" data-testid="add-comment-button">
                Add Comment
              </Button>
            </div>

            <Separator />

            {/* Enhanced Activity and Comments Display */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-700">Activity History</h4>
              
              {/* Combine activities and comments, sort by timestamp */}
              {(() => {
                const allItems = [
                  // Activities (status changes, etc.) - filter out Comment Added to avoid duplicates
                  ...activities.filter(act => act.activity_type !== 'Comment Added').map(act => ({
                    type: 'activity',
                    timestamp: act.timestamp,
                    user_name: act.broker_name,
                    activity_type: act.activity_type,
                    old_value: act.old_value,
                    new_value: act.new_value,
                    next_call_date: act.next_call_date,
                    site_visit_number: act.site_visit_number,
                    site_visit_date: act.site_visit_date,
                    comment: act.comment
                  })),
                  // Regular comments
                  ...((lead.comments || []).map(comment => ({
                    type: 'comment',
                    timestamp: comment.timestamp,
                    user_name: comment.user_name,
                    text: comment.text
                  })))
                ];
                
                // Sort by timestamp (most recent first)
                allItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                
                return allItems.length > 0 ? allItems.map((item, index) => (
                  <div key={index} className="bg-slate-50 p-3 rounded-lg border-l-4 border-l-cyan-500">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-slate-700">{item.user_name}</span>
                      <span className="text-xs text-slate-500">
                        {item.timestamp && format(new Date(item.timestamp), 'dd-MMM-yyyy HH:mm')}
                      </span>
                    </div>
                    
                    {item.type === 'activity' && item.activity_type === 'Status Change' && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-800">
                          Status: <span className="text-red-600">{item.old_value}</span> → <span className="text-green-600">{item.new_value}</span>
                        </p>
                        {item.next_call_date && (
                          <p className="text-slate-600">Next Call Date: {new Date(item.next_call_date).toLocaleDateString('en-GB')}</p>
                        )}
                        {item.site_visit_number && item.site_visit_date && (
                          <p className="text-slate-600">
                            Site Visit: Visit #{item.site_visit_number} on {new Date(item.site_visit_date).toLocaleDateString('en-GB')}
                          </p>
                        )}
                        {item.comment && (
                          <p className="text-slate-600">Comment: {item.comment}</p>
                        )}
                      </div>
                    )}
                    
                    {item.type === 'activity' && item.activity_type === 'Site Visit Recorded' && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-800">
                          Site Visit Recorded: Visit #{item.site_visit_number} on {new Date(item.site_visit_date).toLocaleDateString('en-GB')}
                        </p>
                        {item.comment && (
                          <p className="text-slate-600">Comment: {item.comment}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Next Call Date Updated - for rescheduled follow ups */}
                    {item.type === 'activity' && item.activity_type === 'Next Call Date Updated' && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-800">
                          📅 Follow Up Rescheduled
                        </p>
                        <p className="text-slate-700">
                          <span className="text-red-600">{item.old_value}</span> → <span className="text-green-600">{item.new_value}</span>
                        </p>
                        {item.comment && (
                          <p className="text-slate-600 italic">{item.comment}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Lead Reassigned */}
                    {item.type === 'activity' && item.activity_type === 'Lead Reassigned' && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-800">
                          🔄 Lead Reassigned
                        </p>
                        <p className="text-slate-700">
                          <span className="text-red-600">{item.old_value}</span> → <span className="text-green-600">{item.new_value}</span>
                        </p>
                      </div>
                    )}
                    
                    {/* Generic activity fallback for other types */}
                    {item.type === 'activity' && !['Status Change', 'Site Visit Recorded', 'Next Call Date Updated', 'Lead Reassigned', 'Comment Added'].includes(item.activity_type) && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-800">
                          {item.activity_type}
                        </p>
                        {item.old_value && item.new_value && (
                          <p className="text-slate-700">
                            <span className="text-red-600">{item.old_value}</span> → <span className="text-green-600">{item.new_value}</span>
                          </p>
                        )}
                        {item.comment && (
                          <p className="text-slate-600">{item.comment}</p>
                        )}
                      </div>
                    )}
                    
                    {item.type === 'comment' && (
                      <p className="text-sm text-slate-600">{item.text}</p>
                    )}
                  </div>
                )) : (
                  <p className="text-sm text-slate-500 text-center py-4">No activity or comments yet</p>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {editing && (
          <div className="flex gap-3 pb-4">
            <Button
              onClick={handleUpdate}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="save-button"
            >
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
            <Button 
              onClick={() => { 
                setEditing(false); 
                setFormData(lead);
                setSelectedProjects(lead.projects || []);
              }} 
              variant="outline" 
              data-testid="cancel-button"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            
            {/* Phase 1A: Delete Lead Button (Super Admin only) */}
            {isSuperAdmin && (
              <Button 
                onClick={() => setShowDeleteDialog(true)}
                variant="destructive"
                className="ml-auto"
                data-testid="delete-lead-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Lead
              </Button>
            )}
          </div>
        )}
      </div>

      {/* BUG FIX #3: Enhanced Status Change Dialog (matches LeadList functionality) */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusData.isRescheduling ? 'Reschedule Follow Up' : 'Change Lead Status'}
            </DialogTitle>
            {statusData.isRescheduling && (
              <p className="text-sm text-slate-600 mt-1">
                Update the next call date for this Follow up lead
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Status Selection - Hidden for rescheduling */}
            {!statusData.isRescheduling && (
              <div>
                <Label>Select New Status <span className="text-red-500">*</span></Label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md mt-1"
                >
                  <option value="">Select status...</option>
                  {allowedStatuses
                    .filter(s => s !== lead?.lead_status)
                    .map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                </select>
              </div>
            )}
            
            {/* Show current status info for rescheduling */}
            {statusData.isRescheduling && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Current Status:</strong> Follow up
                </p>
                {lead.next_call_date && (
                  <p className="text-sm text-amber-700 mt-1">
                    <strong>Current Next Call:</strong> {new Date(lead.next_call_date).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            )}

            {/* Conditional fields based on status */}
            {selectedStatus && (
              <>
                {/* Next Call Date for Attempted 1-3, Follow up, Site Visit Planned */}
                {['Attempted - 1', 'Attempted - 2', 'Attempted - 3', 'Follow up', 'Site Visit Planned'].includes(selectedStatus) && (
                  <div>
                    <Label>Next Call Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={statusData.next_call_date}
                      onChange={(e) => setStatusData({ ...statusData, next_call_date: e.target.value })}
                      className="mt-1"
                      min={new Date().toISOString().split('T')[0]} // Prevent past dates
                    />
                  </div>
                )}

                {/* Site Visit Number and Date for Site Visit Done */}
                {selectedStatus === 'Site Visit Done' && (
                  <>
                    <div>
                      <Label>Site Visit # <span className="text-red-500">*</span></Label>
                      <select
                        value={statusData.site_visit_number}
                        onChange={(e) => setStatusData({ ...statusData, site_visit_number: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md mt-1"
                      >
                        <option value="">Select visit number...</option>
                        {/* Sequential visit numbers based on existing visits */}
                        {(() => {
                          const existingVisits = lead?.site_visits || [];
                          const maxVisit = existingVisits.length;
                          const nextVisit = maxVisit + 1;
                          
                          // Only show the next sequential visit number (up to 4 max)
                          if (nextVisit <= 4) {
                            return <option value={nextVisit}>Visit #{nextVisit}</option>;
                          } else {
                            return <option value="" disabled>All visits completed (max 4)</option>;
                          }
                        })()}
                      </select>
                    </div>
                    <div>
                      <Label>Site Visit Date <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={statusData.site_visit_date}
                        onChange={(e) => setStatusData({ ...statusData, site_visit_date: e.target.value })}
                        className="mt-1"
                        max={new Date().toISOString().split('T')[0]} // Allow only past/present dates
                      />
                    </div>
                  </>
                )}

                {/* Tentative Visit Date for Site Visit Planned */}
                {selectedStatus === 'Site Visit Planned' && (
                  <div>
                    <Label>Tentative Visit Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={statusData.tentative_visit_date}
                      onChange={(e) => setStatusData({ ...statusData, tentative_visit_date: e.target.value })}
                      className="mt-1"
                      min={new Date().toISOString().split('T')[0]} // Prevent past dates
                    />
                  </div>
                )}

                {/* Dead Reason dropdown for Lost, Unqualified, Dropped after site visit */}
                {['Lost', 'Unqualified', 'Dropped after site visit'].includes(selectedStatus) && (
                  <div>
                    <Label>Dead Reason <span className="text-red-500">*</span></Label>
                    <Select 
                      value={statusData.dead_reason} 
                      onValueChange={(value) => setStatusData({ ...statusData, dead_reason: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {deadReasons.map(reason => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              </>
            )}
            
            {/* Comment field - shown for all status changes and reschedules */}
            {(selectedStatus || statusData.isRescheduling) && (
              <div>
                <Label>Comment (Optional)</Label>
                <Textarea
                  value={statusData.comment || ''}
                  onChange={(e) => setStatusData({ ...statusData, comment: e.target.value })}
                  placeholder="Add a comment about this status change..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveStatus} className="bg-cyan-600 hover:bg-cyan-700">
              {statusData.isRescheduling ? 'Update Date' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 1A: Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="mr-2 h-5 w-5" />
              Delete Lead Confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to delete this lead?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Lead: <strong>{lead.lead_number} - {lead.customer_name}</strong>
            </p>
            <p className="text-sm text-red-600 mt-3 font-medium">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteLead}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicate Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <GitMerge className="mr-2 h-5 w-5 text-cyan-600" />
              Merge Duplicate Lead
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-medium">Duplicate Lead (will be deleted):</p>
                <p className="text-sm text-slate-600">{lead.lead_number} - {lead.customer_name}</p>
                <p className="text-sm text-slate-600">Mobile: {lead.mobile}</p>
              </div>
              
              <div className="flex items-center justify-center">
                <span className="text-slate-400">↓ Merge into ↓</span>
              </div>
              
              <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
                <p className="text-sm font-medium text-cyan-900">Parent Lead:</p>
                <p className="text-sm text-cyan-700">{lead.parent_lead_number}</p>
                <p className="text-xs text-cyan-600 mt-1">Projects will be merged and duplicate will be deleted</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-600 mt-4">
              This will merge the projects and delete the duplicate lead. This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMergeDuplicate} className="bg-cyan-600 hover:bg-cyan-700">
              <GitMerge className="mr-2 h-4 w-4" />
              Merge Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site Visit Recording Dialog */}
      <Dialog open={showSiteVisitDialog} onOpenChange={setShowSiteVisitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Site Visit</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Recording visit #{siteVisitTracking.selectedVisit} for this lead.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Visit Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={statusData.site_visit_date}
                  onChange={(e) => setStatusData({ ...statusData, site_visit_date: e.target.value })}
                  className="mt-1"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSiteVisitDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (!statusData.site_visit_date) {
                  toast.error('Please select a visit date');
                  return;
                }
                
                const token = localStorage.getItem('token');
                try {
                  await axios.put(`${API}/leads/${id}`, {
                    site_visit_number: parseInt(siteVisitTracking.selectedVisit),
                    site_visit_date: statusData.site_visit_date
                  }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  
                  toast.success('Site visit recorded successfully');
                  setShowSiteVisitDialog(false);
                  setSiteVisitTracking({ selectedVisit: '' });
                  setStatusData({ ...statusData, site_visit_date: '', comment: '' });
                  loadLead();
                  loadActivities();
                } catch (error) {
                  // Handle Pydantic validation errors (array of objects) or string errors
                  let errorMessage = 'Failed to record site visit';
                  const detail = error.response?.data?.detail;
                  if (typeof detail === 'string') {
                    errorMessage = detail;
                  } else if (Array.isArray(detail) && detail.length > 0) {
                    errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
                  } else if (detail && typeof detail === 'object') {
                    errorMessage = detail.msg || detail.message || JSON.stringify(detail);
                  }
                  toast.error(errorMessage);
                }
              }}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Record Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LeadDetail;
